import React, { createContext, useContext, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router";
import { SHELL_PAGES } from "@/lib/navigation/pages";

const PAGE_PATH: Record<string, string> = {
  "/": "Overview",
  "/admin": "AdminInvitations",
  ...Object.fromEntries(
    Object.entries(SHELL_PAGES).map(([key, page]) => [page.path, key]),
  ),
};

const PAGE_ROUTES: Record<string, string> = {
  Admin: "/admin",
  ...Object.fromEntries(
    Object.entries(SHELL_PAGES).map(([key, page]) => [key, page.path]),
  ),
};

interface NavigationContextType {
  selectedPage: string;
  navigateToPage: (page: string) => void;
}

const NavigationContext = createContext<NavigationContextType | undefined>(
  undefined,
);

export const NavigationProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const selectedPage = useMemo((): string => {
    if (PAGE_PATH[location.pathname]) {
      return PAGE_PATH[location.pathname];
    }
    if (location.pathname.startsWith("/apps/")) {
      return "Apps";
    }
    // No rail item, and "Overview" would light a page the user is not on.
    if (
      location.pathname.startsWith("/spaces/") ||
      location.pathname.startsWith("/organizations/")
    ) {
      return "Settings";
    }
    return "Overview";
  }, [location.pathname]);

  const navigateToPage = useCallback(
    (page: string) => {
      if (selectedPage === page) {
        return;
      }

      const targetRoute = PAGE_ROUTES[page] || `/${page.toLowerCase()}`;
      navigate(targetRoute);
    },
    [selectedPage, navigate],
  );

  return (
    <NavigationContext.Provider value={{ selectedPage, navigateToPage }}>
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (context === undefined) {
    throw new Error("useNavigation must be used within a NavigationProvider");
  }
  return context;
};
